import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Briefcase, Mail, Users, Plus, Loader2, X, Search, ExternalLink, Settings, Sparkles, ShieldCheck, Database } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import IronVisionLogo from '../components/IronVisionLogo';
import { adminListJobs, adminCreateJob, adminUpdateJob, adminDeleteJob, adminListLeads, adminListApplications, adminDeleteLead, adminDeleteApplication } from '../api';
import JobsTable from '../components/admin/JobsTable';
import ApplicationsTable from '../components/admin/ApplicationsTable';
import LeadsTable from '../components/admin/LeadsTable';
import CredentialsForm from '../components/admin/CredentialsForm';
import CapabilityCard from '../components/admin/CapabilityCard';
import JobForm from '../components/admin/JobForm';
import NotificationRecipientsCard from '../components/admin/NotificationRecipientsCard';
import UsersCard from '../components/admin/UsersCard';
import AnalysisTab from '../components/admin/AnalysisTab';
import LoginActivityCard from '../components/admin/LoginActivityCard';
import SharedSectionsCard from '../components/admin/SharedSectionsCard';
import SecurityScanCard from '../components/admin/SecurityScanCard';
import ResumeRepositoryTab from '../components/admin/ResumeRepositoryTab';

const emptyJob = { title: '', tag: 'Engineering', description: '', location: 'Remote (US)', type: 'Full-time', salary_range: '', requirements: '', certifications: '', benefits: '', about: '', eeo: '', unlocked_fields: [], clearance_level: 'None', active: true };

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [j, l, a] = await Promise.all([adminListJobs(), adminListLeads(), adminListApplications()]);
      setJobs(j); setLeads(l); setApps(a);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast({ title: 'Session expired', description: 'Please sign in again.', variant: 'destructive' });
        logout(); navigate('/admin/login', { replace: true });
        return;
      }
      toast({ title: 'Failed to load', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast, logout, navigate]);

  useEffect(() => { reload(); }, [reload]);

  const openNew = () => { setEditJob({ ...emptyJob }); setEditorOpen(true); };
  const openEdit = (j) => { setEditJob({ ...j }); setEditorOpen(true); };

  const saveJob = async () => {
    if (!editJob.title || !editJob.description) {
      toast({ title: 'Missing fields', description: 'Title and description are required.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editJob.id) await adminUpdateJob(editJob.id, editJob); else await adminCreateJob(editJob);
      toast({ title: editJob.id ? 'Job updated' : 'Job created' });
      setEditorOpen(false); setEditJob(null);
      await reload();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const removeJob = async (id) => {
    try { await adminDeleteJob(id); toast({ title: 'Job deleted' }); setConfirmDel(null); await reload(); }
    catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Delete lead from ${lead.name}?`)) return;
    try { await adminDeleteLead(lead.id); toast({ title: 'Lead deleted' }); await reload(); }
    catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
  };

  const removeApp = async (a) => {
    if (!window.confirm(`Delete application from ${a.name}? Resume will also be removed.`)) return;
    try { await adminDeleteApplication(a.id); toast({ title: 'Application deleted' }); await reload(); }
    catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }); }
  };

  const filteredApps = apps.filter(a => !q || a.name.toLowerCase().includes(q.toLowerCase()) || a.email.toLowerCase().includes(q.toLowerCase()) || (a.job_title || '').toLowerCase().includes(q.toLowerCase()));
  const filteredLeads = leads.filter(l => !q || l.name.toLowerCase().includes(q.toLowerCase()) || l.email.toLowerCase().includes(q.toLowerCase()) || (l.company || '').toLowerCase().includes(q.toLowerCase()));

  const doLogout = () => { logout(); navigate('/admin/login', { replace: true }); };

  const stats = [
    { key: 'active-jobs', icon: Briefcase, label: 'Active Jobs', val: jobs.filter(j => j.active).length, total: jobs.length },
    { key: 'applications', icon: Users, label: 'Applications', val: apps.length },
    { key: 'leads', icon: Mail, label: 'Leads', val: leads.length },
  ];

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white">
      <header className="sticky top-0 z-40 bg-[#0a0e1a]/85 backdrop-blur border-b border-cyan-400/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2.5">
            <IronVisionLogo size={36} />
            <span className="text-white font-bold tracking-wider">IRON<span className="text-cyan-400">VISION</span> <span className="text-slate-500 font-normal ml-1">/ Admin</span></span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Public site</a>
            <span className="text-xs text-slate-400 hidden sm:inline">{user?.username}</span>
            <Button onClick={doLogout} variant="outline" className="h-9 border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200"><LogOut className="w-4 h-4 mr-1" /> Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {stats.map(s => (
            <div key={s.key} className="rounded-xl border border-cyan-400/10 bg-slate-900/40 p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><s.icon className="w-5 h-5 text-cyan-400" /></div>
              <div>
                <p className="text-2xl font-bold text-white">{s.val}{s.total !== undefined && <span className="text-slate-500 text-sm font-normal"> / {s.total}</span>}</p>
                <p className="text-xs text-slate-400">{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <TabsList className="bg-slate-900/60 border border-cyan-400/10">
              <TabsTrigger value="jobs" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900">Jobs</TabsTrigger>
              <TabsTrigger value="applications" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900">Applications</TabsTrigger>
              <TabsTrigger value="analysis" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900"><Sparkles className="w-3.5 h-3.5 mr-1 inline" />Resume Analysis</TabsTrigger>
              <TabsTrigger data-testid="admin-tab-repository" value="repository" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900"><Database className="w-3.5 h-3.5 mr-1 inline" />Resume Repository</TabsTrigger>
              <TabsTrigger value="leads" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900">Leads</TabsTrigger>
              {user?.role === 'owner' && <TabsTrigger data-testid="admin-tab-security" value="security" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900"><ShieldCheck className="w-3.5 h-3.5 mr-1 inline" />Security</TabsTrigger>}
              <TabsTrigger value="settings" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-slate-900"><Settings className="w-3.5 h-3.5 mr-1 inline" />Settings</TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2">
              {(tab === 'applications' || tab === 'leads') && (
                <div className="relative">
                  <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search..." className="pl-9 bg-[#080d18] border-slate-700 focus:border-cyan-400 text-white w-56" />
                </div>
              )}
              {tab === 'jobs' && (
                <Button onClick={openNew} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold rounded-md"><Plus className="w-4 h-4 mr-1" /> New Job</Button>
              )}
            </div>
          </div>

          <TabsContent value="jobs">
            <div className="rounded-xl border border-cyan-400/10 bg-slate-900/30 overflow-hidden">
              <JobsTable jobs={jobs} loading={loading} onEdit={openEdit} onDelete={setConfirmDel} onChanged={reload} />
            </div>
          </TabsContent>

          <TabsContent value="applications">
            <div className="rounded-xl border border-cyan-400/10 bg-slate-900/30 overflow-hidden">
              <ApplicationsTable apps={filteredApps} loading={loading} onDelete={removeApp} />
            </div>
          </TabsContent>

          <TabsContent value="analysis">
            <AnalysisTab apps={apps} loading={loading} />
          </TabsContent>

          <TabsContent value="leads">
            <div className="rounded-xl border border-cyan-400/10 bg-slate-900/30 overflow-hidden">
              <LeadsTable leads={filteredLeads} loading={loading} onDelete={removeLead} />
            </div>
          </TabsContent>

          {user?.role === 'owner' && (
            <TabsContent value="security">
              <div className="space-y-6">
                <SecurityScanCard />
                <LoginActivityCard />
              </div>
            </TabsContent>
          )}

          <TabsContent value="repository">
            <ResumeRepositoryTab />
          </TabsContent>

          <TabsContent value="settings">
            <div className="max-w-2xl space-y-6">
              <SharedSectionsCard />
              <CapabilityCard />
              <NotificationRecipientsCard />
              {user?.role === 'owner' && <UsersCard />}
              <CredentialsForm />
            </div>
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="bg-[#0d1424] border border-cyan-400/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">{editJob?.id ? 'Edit Job' : 'New Job'}</DialogTitle>
            <DialogDescription className="text-slate-400">Manage job posting visible on the public Careers page.</DialogDescription>
          </DialogHeader>
          {editJob && <JobForm job={editJob} onChange={setEditJob} />}
          <DialogFooter className="mt-4">
            <Button onClick={() => setEditorOpen(false)} variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200"><X className="w-4 h-4 mr-1" /> Cancel</Button>
            <Button onClick={saveJob} disabled={saving} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold">{saving ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Saving...</> : 'Save Job'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={v => !v && setConfirmDel(null)}>
        <DialogContent className="bg-[#0d1424] border border-red-900/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white">Delete job?</DialogTitle>
            <DialogDescription className="text-slate-400">This will permanently remove &quot;{confirmDel?.title}&quot;. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setConfirmDel(null)} variant="outline" className="border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200">Cancel</Button>
            <Button onClick={() => removeJob(confirmDel.id)} className="bg-red-500 hover:bg-red-400 text-white font-semibold">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
